import React, {
  useContext,
  useState,
  useCallback,
  MouseEventHandler,
  CSSProperties,
  useEffect,
  useMemo,
} from 'react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import uuid from 'uuid/v1';
import {
  Box,
  Button,
  Icon,
  Icons,
  Grid,
  Paper,
  InputAdornment,
  Tooltip,
  styled,
} from '@nextiva/next-ui-components';
import { screenEntities } from 'containers/pages/DataImport/ImportTile';
import { getLocksForAllEntities } from 'services';
import colors from 'styles/application/_base/_variables.scss';
import { FeatureContext } from 'components/AccessResolvers/featureContext';

interface style {
  editImportName: string;
}
interface ButtonStyle {
  actionName: string;
}
import {
  StyledSearchTextField,
  StyledSearchIcon,
  StyledClearIcon,
} from '../SearchBar';

const Label = styled('span')`
  color: ${colors.colorPrimary};
`;

const SVG = styled('div')`
  position: relative;
  & > svg {
    cursor: pointer;
    fill: ${({ theme }) => theme.palette.blueGrey.A400};
  }
`;

const StyledPaper = styled(Paper)<style>`
  width: 210px;
  position: absolute;
  right: 0;
  top: 42px;
  z-index: 1;
  div.MuiBox-root {
    height: 40px;
    font-size: 14px;
    letter-spacing: 0.25px;
    line-height: 20px;
    padding: 10px 16px;
    cursor: pointer;
    color: ${({ theme }) => theme.palette.blueGrey[900]};
    svg {
      margin-right: 8px;
      width: 14px;
      height: 14px;
      fill: ${({ theme }) => theme.palette.blueGrey.A400};
    }
  }
`;

const ImportButton = styled(Button)`
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 0px 4px 4px 0px;
  margin-left: 1px;
  position: relative;
  &.MuiButton-sizeMedium {
    padding: 1em;
  }
`;

export const defaultButtonStyle = {
  width: '111px',
  height: '40px',
  borderRadius: '4px 0px 0px 4px',
  marginLeft: '16px',
  padding: '0.1rem',
};
const StyledButton = styled(Button)<ButtonStyle>`
  min-width: ${({ actionName }) => actionName === 'Add location' && '131px'};
`;

interface ImportDropdownOptions {
  importName: string;
  editImportName?: string;
}
interface Props {
  downloadCSV?: MouseEventHandler<SVGSVGElement>;
  actionName: string;
  id?: string;
  label: string;
  placeholder?: string;
  searchData?: (value: string) => void;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  buttonStyle?: CSSProperties;
  disabled?: boolean;
  searchFieldDisabled?: boolean;
  buttonDisabled?: boolean;
  'data-testid': string;
  importDropdownOptions: ImportDropdownOptions[];
}

const SearchBarWrapper = ({
  label = '',
  placeholder = '',
  id = uuid(),
  searchData,
  actionName = '',
  onClick = () => undefined,
  buttonStyle = defaultButtonStyle,
  disabled = false,
  searchFieldDisabled = false,
  buttonDisabled = false,
  downloadCSV,
  'data-testid': dataTestId,
  importDropdownOptions,
}: Props) => {
  const { corpAcctNbr } = useContext(FeatureContext);
  const [val, setValue] = useState('');
  const [dropdown, setDropdown] = useState(false);
  const [isImportInProgress, setIsImportInProgress] = useState(false);
  const { data, refetch: refetchJobLocks } = useQuery('jobLocks', () =>
    getLocksForAllEntities(corpAcctNbr),
  );

  const { jobLocks } = useMemo(
    () => ({
      jobLocks: data?.data,
    }),
    [data?.data],
  );

  const {
    featureState: { bulkImports, bulkUsersEdit } = {
      bulkImports: false,
      bulkUsersEdit: false,
    },
  } = useContext(FeatureContext);
  const history = useHistory();

  useEffect(() => {
    if (
      (history.location.pathname === '/accountinfo' &&
        jobLocks?.includes(screenEntities.LOCATION_CREATE)) ||
      (history.location.pathname === '/teams' &&
        jobLocks?.includes(screenEntities.TEAM_CREATE)) ||
      (history.location.pathname === '/users' &&
        (jobLocks?.includes(screenEntities.USER_CREATE) ||
          jobLocks?.includes(screenEntities.USER_UPDATE)))
    ) {
      setIsImportInProgress(true);
    } else {
      setIsImportInProgress(false);
    }
  }, [history, jobLocks]);

  const toggleDropdown = () => {
    setDropdown((state) => !state);
    void refetchJobLocks();
  };

  const directToImport = (importName) => {
    if (bulkImports) {
      history.push({
        pathname: '/data-import',
        state: importName === 'Bulk edit existing users' ? `${id}_UPDATE` : id,
      });
    } else {
      window.location.href = `${window.location.origin}/apps/integrations/`;
    }
  };

  const clearHandler = () => {
    setValue('');
    searchData('');
    setDropdown(false);
  };

  const onChangeHandler = useCallback(
    (e) => {
      if (e) {
        setValue(e.target.value.trim());
        searchData(e.target.value.trim());
      }
      setDropdown(false);
    },
    [setValue, searchData],
  );

  const getGray = (theme) => `${theme.palette.blueGrey.A300} !important`;

  return (
    <Grid container spacing={0}>
      <Grid item xs={6}>
        <Label
          className="Heading2"
          data-testid="PlatformAdminUI-SearchBar-Header"
        >
          {label}
        </Label>
      </Grid>
      <Grid
        container
        flexDirection="row"
        justifyContent="flex-end"
        alignItems="center"
        item
        flexWrap="nowrap"
        xs={6}
        data-testid="PlatformAdminUI-SearchBar-SearchField"
      >
        <SVG>
          <Tooltip placement="bottom" title="Download CSV File">
            <Icon
              data-testid="PlatformAdminUI-ManageUsers-SearchBar-DownloadIcon"
              icon={Icons.download}
              size={24}
              role="button"
              onClick={downloadCSV}
            />
          </Tooltip>
        </SVG>
        <StyledSearchTextField
          hiddenLabel
          id={id}
          value={val}
          fullWidth
          placeholder={placeholder}
          onChange={onChangeHandler}
          sx={{ width: '60%', marginLeft: '16px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <StyledSearchIcon icon={Icons.search} />
              </InputAdornment>
            ),
            endAdornment: val && (
              <InputAdornment position="end">
                <StyledClearIcon
                  icon={Icons.close}
                  onClick={clearHandler}
                  data-testid={dataTestId}
                />
              </InputAdornment>
            ),
          }}
          data-testid={dataTestId}
          disabled={searchFieldDisabled || disabled}
        />

        <StyledButton
          id="searchbarButton"
          disabled={buttonDisabled || disabled}
          color="primary"
          variant="contained"
          onClick={onClick}
          style={buttonStyle}
          data-testid="PlatformAdminUI-SearchBar-AddButton"
          actionName={actionName}
        >
          {actionName}
        </StyledButton>
        <ImportButton
          disabled={buttonDisabled || disabled}
          color="primary"
          variant="contained"
          onClick={toggleDropdown}
          data-testid="PlatformAdminUI-SearchBar-DropDownButton"
        >
          <Icon
            data-testid="PlatformAdminUI-SearchBar-DownIcon"
            icon={Icons.chevronDown}
            size={16}
            role="button"
          />
        </ImportButton>
      </Grid>
      {dropdown && (
        <Tooltip
          placement="top"
          title="Import in progress"
          sx={{
            backgroundColor: (theme) => theme.palette.secondary.contrastText,
            color: (theme) => theme.palette.danger.contrastText,
            borderRadius: '4px',
            width: '165px',
            height: '40px',
            display: isImportInProgress ? 'flex' : 'none',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <>
            {importDropdownOptions.map((option, index) => (
              <StyledPaper
                variant="elevation"
                editImportName={option.editImportName}
                key={index}
              >
                <Tooltip
                  title={isImportInProgress ? 'Import in progress' : ''}
                  placement="top"
                >
                  <span>
                    <Box
                      alignItems="center"
                      component="div"
                      display="flex"
                      height={56}
                      onClick={directToImport}
                      width="100%"
                      data-testid={`PlatformAdminUI-SearchBar-Upload-${index}`}
                      sx={
                        isImportInProgress && {
                          pointerEvents: 'none',
                          color: getGray,
                        }
                      }
                    >
                      <Icon
                        icon={Icons.upload}
                        size={16}
                        role="button"
                        sx={
                          isImportInProgress && {
                            fill: getGray,
                          }
                        }
                      />
                      {option.importName}
                    </Box>
                  </span>
                </Tooltip>

                {option.editImportName && bulkUsersEdit && (
                  <Tooltip
                    title={isImportInProgress ? 'Import in progress' : ''}
                    placement="top"
                  >
                    <span>
                      <Box
                        alignItems="center"
                        component="div"
                        display="flex"
                        height={40}
                        onClick={() => directToImport(option.editImportName)}
                        justifyContent="left"
                        width="100%"
                        data-testid={`PlatformAdminUI-Edit-Users-Import-${index}`}
                        sx={
                          isImportInProgress && {
                            pointerEvents: 'none',
                            color: getGray,
                          }
                        }
                      >
                        <Icon
                          icon={Icons.upload}
                          role="button"
                          sx={
                            isImportInProgress && {
                              fill: getGray,
                            }
                          }
                        />
                        {option.editImportName}
                      </Box>
                    </span>
                  </Tooltip>
                )}
              </StyledPaper>
            ))}
          </>
        </Tooltip>
      )}
    </Grid>
  );
};

export default React.memo(SearchBarWrapper);
